import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { 
  FileText, Plus, Eye, Trash2, Edit2, Save, X, CheckCircle, 
  Clock, BarChart3, Users, ClipboardList, Star, Download,
  ChevronDown, ChevronUp, RefreshCw, Filter, Search
} from 'lucide-react';

const VisitReportsSystem = () => {
  // State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(null);
  const [formData, setFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [templatesRes, submissionsRes, statsRes] = await Promise.all([
        axiosInstance.get('/visit-reports/templates'),
        axiosInstance.get('/visit-reports/submissions'),
        axiosInstance.get('/visit-reports/stats/summary')
      ]);
      setTemplates(templatesRes.data);
      setSubmissions(submissionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Initialize default templates
  const initializeTemplates = async () => {
    try {
      setLoading(true);
      await axiosInstance.post('/visit-reports/templates/init');
      toast.success('Templates initialized');
      fetchData();
    } catch (error) {
      // Templates may already exist
      fetchData();
    }
  };

  // Handle form submission
  const handleSubmitReport = async () => {
    if (!selectedTemplate) return;
    
    try {
      const visitId = `VISIT-${Date.now()}`;
      await axiosInstance.post('/visit-reports/submissions', {
        visit_id: visitId,
        template_id: selectedTemplate.id,
        submitted_data: formData,
        customer_name: formData.customer_name || 'Customer',
        visit_date: new Date().toISOString().split('T')[0],
        status: 'submitted'
      });
      toast.success('Report submitted successfully!');
      setShowFormModal(false);
      setFormData({});
      setSelectedTemplate(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  // Delete submission
  const handleDeleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    try {
      await axiosInstance.delete(`/visit-reports/submissions/${id}`);
      toast.success('Report deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  // Render field based on type
  const renderField = (field) => {
    const value = formData[field.name] || '';
    const commonProps = {
      id: field.name,
      name: field.name,
      className: "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
      required: field.required
    };

    // Check visibility condition
    if (field.visible_if) {
      const conditionField = field.visible_if.field;
      const conditionValue = field.visible_if.value;
      if (formData[conditionField] !== conditionValue && 
          formData[conditionField] !== String(conditionValue) &&
          formData[conditionField] !== (conditionValue ? 'true' : 'false')) {
        return null;
      }
    }

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            {...commonProps}
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          />
        );

      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={3}
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            {...commonProps}
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          />
        );

      case 'currency':
        return (
          <div className="flex">
            <input
              type="number"
              {...commonProps}
              className={`${commonProps.className} rounded-r-none`}
              value={value}
              step="0.01"
              min="0"
              onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
            />
            <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-gray-600">
              SAR
            </span>
          </div>
        );

      case 'select':
        return (
          <select
            {...commonProps}
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          >
            <option value="">Select...</option>
            {field.options?.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((opt, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={field.name}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-gray-700">{opt}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => setFormData({ ...formData, [field.name]: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">Yes</span>
          </label>
        );

      case 'boolean':
        return (
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={field.name}
                value="true"
                checked={value === true || value === 'true'}
                onChange={() => setFormData({ ...formData, [field.name]: true })}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-700">Yes - نعم</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={field.name}
                value="false"
                checked={value === false || value === 'false'}
                onChange={() => setFormData({ ...formData, [field.name]: false })}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-700">No - لا</span>
            </label>
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            {...commonProps}
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          />
        );

      case 'rating':
        const max = field.max || 5;
        return (
          <div className="flex gap-1">
            {[...Array(max)].map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setFormData({ ...formData, [field.name]: i + 1 })}
                className={`text-2xl transition-colors ${
                  (value || 0) > i ? 'text-yellow-400' : 'text-gray-300'
                } hover:text-yellow-400`}
              >
                ★
              </button>
            ))}
            <span className="ml-2 text-gray-600">({value || 0}/{max})</span>
          </div>
        );

      default:
        return (
          <input
            type="text"
            {...commonProps}
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          />
        );
    }
  };

  // Filter submissions
  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = !searchTerm || 
      (sub.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (sub.template_name?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterType === 'all' || sub.status === filterType;
    return matchesSearch && matchesFilter;
  });

  // Dashboard Component
  const Dashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Reports</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.total_submissions || 0}</p>
            </div>
            <ClipboardList className="w-10 h-10 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Submitted</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.by_status?.submitted || 0}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Drafts</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.by_status?.draft || 0}</p>
            </div>
            <Clock className="w-10 h-10 text-yellow-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">This Week</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.recent_submissions || 0}</p>
            </div>
            <BarChart3 className="w-10 h-10 text-purple-500" />
          </div>
        </div>
      </div>

      {/* By Type Chart */}
      {stats?.by_type && Object.keys(stats.by_type).length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Reports by Type</h3>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(stats.by_type).map(([type, count]) => (
              <div key={type} className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{count}</p>
                <p className="text-sm text-gray-600 capitalize">{type || 'Unknown'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Submissions */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-900">Recent Reports</h3>
          <button 
            onClick={() => setActiveTab('reports')}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            View All →
          </button>
        </div>
        <div className="space-y-3">
          {submissions.slice(0, 5).map((sub) => (
            <div 
              key={sub.id} 
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
              onClick={() => setShowViewModal(sub)}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  sub.status === 'submitted' ? 'bg-green-100' : 'bg-yellow-100'
                }`}>
                  <FileText className={`w-5 h-5 ${
                    sub.status === 'submitted' ? 'text-green-600' : 'text-yellow-600'
                  }`} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{sub.customer_name || 'Customer'}</p>
                  <p className="text-sm text-gray-500">{sub.template_name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">{sub.visit_date}</p>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  sub.status === 'submitted' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {sub.status}
                </span>
              </div>
            </div>
          ))}
          {submissions.length === 0 && (
            <p className="text-center text-gray-500 py-8">No reports yet</p>
          )}
        </div>
      </div>
    </div>
  );

  // Templates List Component
  const TemplatesList = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-gray-900">Available Templates</h3>
        <button
          onClick={initializeTemplates}
          className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh Templates
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {templates.map((template) => (
          <div 
            key={template.id} 
            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
          >
            <div className={`p-4 ${
              template.template_type === 'sales' ? 'bg-blue-600' :
              template.template_type === 'service' ? 'bg-green-600' :
              template.template_type === 'collection' ? 'bg-purple-600' : 'bg-gray-600'
            } text-white`}>
              <h4 className="font-bold text-lg">{template.template_name}</h4>
              <p className="text-sm opacity-80 capitalize">{template.template_type} Report</p>
            </div>
            <div className="p-4">
              <p className="text-gray-600 text-sm mb-4">
                {template.form_structure?.sections?.length || 0} sections • 
                {template.form_structure?.sections?.reduce((acc, s) => acc + (s.fields?.length || 0), 0) || 0} fields
              </p>
              <button
                onClick={() => {
                  setSelectedTemplate(template);
                  setFormData({ customer_name: '' });
                  setShowFormModal(true);
                }}
                className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Use Template
              </button>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="col-span-3 text-center py-12 bg-gray-50 rounded-xl">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No templates available</p>
            <button
              onClick={initializeTemplates}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Initialize Default Templates
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Reports List Component
  const ReportsList = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center bg-white p-4 rounded-xl shadow">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="submitted">Submitted</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
        </select>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Report</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredSubmissions.map((sub) => (
              <tr key={sub.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{sub.template_name}</p>
                  <p className="text-xs text-gray-500">ID: {sub.visit_id}</p>
                </td>
                <td className="px-4 py-3 text-gray-700">{sub.customer_name || '-'}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs capitalize">
                    {templates.find(t => t.id === sub.template_id)?.template_type || 'Unknown'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">{sub.visit_date || sub.submitted_at?.split('T')[0]}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    sub.status === 'submitted' ? 'bg-green-100 text-green-700' :
                    sub.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                    sub.status === 'approved' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {sub.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => setShowViewModal(sub)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="View"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSubmission(sub.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredSubmissions.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p>No reports found</p>
          </div>
        )}
      </div>
    </div>
  );

  // Main Render
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Visit Reports System</h1>
        <p className="text-gray-600">نظام تقارير الزيارات المتقدم</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
          { id: 'templates', label: 'Templates', icon: FileText },
          { id: 'reports', label: 'Reports', icon: ClipboardList }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id 
                ? 'text-blue-600 border-blue-600' 
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-5 h-5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'dashboard' && <Dashboard />}
      {activeTab === 'templates' && <TemplatesList />}
      {activeTab === 'reports' && <ReportsList />}

      {/* Form Modal */}
      {showFormModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-blue-600 text-white p-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold">{selectedTemplate.template_name}</h3>
                <p className="text-sm text-blue-100">Fill out the report form</p>
              </div>
              <button
                onClick={() => { setShowFormModal(false); setSelectedTemplate(null); setFormData({}); }}
                className="hover:bg-blue-700 p-2 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Customer Name */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Name - اسم العميل <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.customer_name || ''}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Form Sections */}
              {selectedTemplate.form_structure?.sections?.map((section, sIdx) => (
                <div key={sIdx} className="mb-6">
                  <h4 className="font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    {section.title}
                  </h4>
                  <div className="space-y-4">
                    {section.fields?.map((field, fIdx) => {
                      const renderedField = renderField(field);
                      if (!renderedField) return null;
                      return (
                        <div key={fIdx}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                          </label>
                          {renderedField}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 bg-gray-50 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowFormModal(false); setSelectedTemplate(null); setFormData({}); }}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReport}
                disabled={!formData.customer_name}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-green-600 text-white p-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold">{showViewModal.template_name}</h3>
                <p className="text-sm text-green-100">{showViewModal.customer_name}</p>
              </div>
              <button
                onClick={() => setShowViewModal(null)}
                className="hover:bg-green-700 p-2 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Visit ID:</span>
                    <span className="ml-2 font-medium">{showViewModal.visit_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Date:</span>
                    <span className="ml-2 font-medium">{showViewModal.visit_date || showViewModal.submitted_at?.split('T')[0]}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      showViewModal.status === 'submitted' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {showViewModal.status}
                    </span>
                  </div>
                </div>
              </div>

              <h4 className="font-bold text-gray-900 mb-3">Submitted Data</h4>
              <div className="space-y-2">
                {Object.entries(showViewModal.submitted_data || {}).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="font-medium text-gray-900">
                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value || '-')}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-gray-50 border-t flex justify-end">
              <button
                onClick={() => setShowViewModal(null)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitReportsSystem;
